import { motion } from 'framer-motion';
import { ArrowRight, Target, ShieldAlert, ShieldCheck, HelpCircle, TrendingDown, TrendingUp } from 'lucide-react';
import type { ExposureResult } from '@/types/walletIntel';

function shorten(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
}

function Chain({ direction, seedAddress, exposure }: {
  direction: 'Incoming' | 'Outgoing';
  seedAddress: string;
  exposure: ExposureResult;
}) {
  const hop = direction === 'Incoming' ? exposure.closest_incoming_exposure : exposure.closest_outgoing_exposure;
  const Icon = direction === 'Incoming' ? TrendingDown : TrendingUp;
  return (
    <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '16px 18px' }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} style={{ color: '#7C8CA6' }} />
        <p className="tracking-[0.08em]" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>
          {direction === 'Incoming' ? 'MONEY FLOWING IN' : 'MONEY FLOWING OUT'}
        </p>
      </div>
      <p style={{ color: '#5A7A9A', fontSize: 12, marginBottom: 14, lineHeight: 1.5 }}>
        {direction === 'Incoming'
          ? 'Walking backwards through who sent funds to this wallet.'
          : 'Walking forwards through who this wallet sent funds to.'}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <div style={{ background: '#0F2030', border: '1px solid #1E3A5F', borderRadius: 10, padding: '11px 15px', minWidth: 170 }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Target size={13} style={{ color: '#00C8FF' }} />
            <span style={{ color: '#00C8FF', fontSize: 11, fontWeight: 700 }}>HOP 0 · THIS WALLET</span>
          </div>
          <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 12 }}>{shorten(seedAddress)}</span>
        </div>
        {hop ? (
          <>
            <ArrowRight size={18} style={{ color: '#5A7A9A' }} />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              style={{ background: '#1A0000', border: '1.5px solid #FF1F1F', borderRadius: 10, padding: '11px 15px', minWidth: 170, boxShadow: '0 0 16px #FF1F1F30' }}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <ShieldAlert size={13} style={{ color: '#FF1F1F' }} />
                <span style={{ color: '#FF1F1F', fontSize: 11, fontWeight: 700 }}>
                  {hop.hop === 0 ? 'DIRECT MATCH' : `${hop.hop} HOP${hop.hop > 1 ? 'S' : ''} AWAY`}
                </span>
              </div>
              <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 12 }}>{shorten(hop.matched_address)}</span>
              <p style={{ color: '#FF6B6B', fontSize: 11, marginTop: 5 }}>
                {hop.matches[0]?.category?.replace(/_/g, ' ') ?? 'flagged'}
              </p>
            </motion.div>
          </>
        ) : (
          <>
            <ArrowRight size={18} style={{ color: '#5A7A9A' }} />
            <div style={{ background: '#001F0F', border: '1px solid #00C85340', borderRadius: 10, padding: '11px 15px', display: 'flex', alignItems: 'center', gap: 9 }}>
              <ShieldCheck size={16} style={{ color: '#00C853' }} />
              <span style={{ color: '#00C853', fontSize: 12.5 }}>
                No known-bad address found within {exposure.hops_examined} hop{exposure.hops_examined === 1 ? '' : 's'}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface Props {
  exposure: ExposureResult;
  seedAddress: string;
  maxHops: number;
  onMaxHopsChange: (n: number) => void;
  isFetching?: boolean;
}

export function ExposureChain({ exposure, seedAddress, maxHops, onMaxHopsChange, isFetching }: Props) {
  const flagged = !!(exposure.closest_incoming_exposure || exposure.closest_outgoing_exposure);
  const closestHop = Math.min(
    exposure.closest_incoming_exposure?.hop ?? Infinity,
    exposure.closest_outgoing_exposure?.hop ?? Infinity,
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Explainer */}
      <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '16px 18px', display: 'flex', gap: 11 }}>
        <HelpCircle size={17} style={{ color: '#00C8FF', flexShrink: 0, marginTop: 1 }} />
        <div>
          <p style={{ color: '#F2F6FC', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
            What "exposure" means here
          </p>
          <p style={{ color: '#8D96AC', fontSize: 12.5, lineHeight: 1.65 }}>
            This wallet might not be flagged itself, but money is fungible — if it received funds that trace back a few hops
            to a sanctioned or scam address, that's still a real compliance risk. We walk outward from this wallet through its
            real on-chain counterparties (both who paid it, and who it paid), checking every address we pass against Sycek's
            own blocklist. "Hop 1" means a direct counterparty was flagged; "Hop 2" means a counterparty-of-a-counterparty was.
            <strong style={{ color: '#DCE8F5' }}> A clean result means nothing bad was found within this depth</strong> — it
            doesn't prove nothing exists further out, which is why depth is adjustable rather than unlimited (each extra hop
            costs real API calls against a live data provider).
          </p>
        </div>
      </div>

      {/* Verdict summary */}
      <div
        className="flex items-center gap-3 px-4 py-3.5 rounded-xl"
        style={{
          background: flagged ? '#1A0000' : '#001F0F',
          border: `1px solid ${flagged ? '#FF1F1F40' : '#00C85340'}`,
        }}
      >
        {flagged ? <ShieldAlert size={19} style={{ color: '#FF1F1F' }} /> : <ShieldCheck size={19} style={{ color: '#00C853' }} />}
        <span style={{ color: flagged ? '#FF1F1F' : '#00C853', fontWeight: 700, fontSize: 14 }}>
          {flagged
            ? `Indirect exposure found — ${closestHop} hop${closestHop === 1 ? '' : 's'} from a known-bad address`
            : `No indirect exposure found within ${exposure.hops_examined} hop${exposure.hops_examined === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <span style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 600 }}>SEARCH DEPTH</span>
        <div className="flex items-center gap-1.5">
          {[1, 2].map((n) => {
            const active = maxHops === n;
            return (
              <motion.button
                key={n}
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={() => onMaxHopsChange(n)}
                className="px-3.5 py-1.5 rounded-lg font-bold transition-all"
                style={{
                  background: active ? '#00C8FF18' : '#0C1118',
                  color: active ? '#00C8FF' : '#8D96AC',
                  border: `1px solid ${active ? '#00C8FF44' : '#182030'}`,
                  fontSize: 12,
                }}
              >
                {n} HOP{n > 1 ? 'S' : ''}
              </motion.button>
            );
          })}
        </div>
        {isFetching && <span style={{ color: '#5A7A9A', fontSize: 11.5 }}>Re-running…</span>}
        <span style={{ color: '#3A5A7A', fontSize: 11, marginLeft: 'auto' }}>
          Each extra hop takes more live checks — kept opt-in
        </span>
      </div>
      <Chain direction="Incoming" seedAddress={seedAddress} exposure={exposure} />
      <Chain direction="Outgoing" seedAddress={seedAddress} exposure={exposure} />
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'HOPS EXAMINED', value: exposure.hops_examined },
          { label: 'ADDRESSES EXAMINED', value: exposure.addresses_examined },
          { label: 'CHECKS RUN', value: exposure.breadcrumbs_calls_used },
        ].map((s) => (
          <motion.div key={s.label} whileHover={{ y: -2 }} style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ color: '#00C8FF', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>{s.value}</div>
            <div style={{ color: '#7C8CA6', fontSize: 10.5, marginTop: 3, fontWeight: 600 }}>{s.label}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
